import { api } from '@/lib/api';
import type {
    JournalTemplate,
    JournalEntry,
    JournalStats,
    MealLog,
    MealStats,
    DailyPointsSummary,
    CreateJournalEntryRequest,
    CreateMealLogRequest,
    JournalType,
} from '../types/journal-api';

// Uses the shared api instance from lib/api so journal/meal requests go through
// the same auth + single-flight token-refresh interceptor as the rest of the app.

// Journal Templates API
export const journalTemplatesApi = {
    async getAll(
        type?: JournalType,
        courseId?: string,
        isActive?: boolean,
    ): Promise<JournalTemplate[]> {
        const params = new URLSearchParams();
        if (type) params.append('type', type);
        if (courseId) params.append('courseId', courseId);
        if (isActive !== undefined) params.append('isActive', isActive.toString());

        const response = await api.get(`/journal-templates?${params.toString()}`);
        return response.data;
    },

    async getById(id: string): Promise<JournalTemplate> {
        const response = await api.get(`/journal-templates/${id}`);
        return response.data;
    },

    async getDefaultDaily(): Promise<JournalTemplate | null> {
        const response = await api.get('/journal-templates/default-daily');
        // With no default template Nest answers 200 with an empty body, which
        // axios hands back as "" rather than null (#106).
        return response.data || null;
    },

    async getForCourse(courseId: string): Promise<JournalTemplate[]> {
        const response = await api.get(`/journal-templates/course/${courseId}`);
        return response.data;
    },
};

// Journal Entries API
export const journalEntriesApi = {
    async create(data: CreateJournalEntryRequest): Promise<JournalEntry> {
        const response = await api.post('/journal-entries', data);
        return response.data;
    },

    async getMyEntries(params?: {
        templateId?: string;
        courseId?: string;
        startDate?: string;
        endDate?: string;
        isCompleted?: boolean;
    }): Promise<JournalEntry[]> {
        const searchParams = new URLSearchParams();
        if (params?.templateId) searchParams.append('templateId', params.templateId);
        if (params?.courseId) searchParams.append('courseId', params.courseId);
        if (params?.startDate) searchParams.append('startDate', params.startDate);
        if (params?.endDate) searchParams.append('endDate', params.endDate);
        if (params?.isCompleted !== undefined)
            searchParams.append('isCompleted', params.isCompleted.toString());

        const response = await api.get(`/journal-entries/my-entries?${searchParams.toString()}`);
        return response.data;
    },

    async getMyStats(startDate?: string, endDate?: string): Promise<JournalStats> {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);

        const response = await api.get(`/journal-entries/my-stats?${params.toString()}`);
        return response.data;
    },

    async getMyEntriesByDate(date: string, templateId?: string): Promise<JournalEntry[]> {
        const params = templateId ? `?templateId=${templateId}` : '';
        const response = await api.get(`/journal-entries/my-entries-by-date/${date}${params}`);
        return response.data;
    },

    async getById(id: string): Promise<JournalEntry> {
        const response = await api.get(`/journal-entries/${id}`);
        return response.data;
    },

    async update(id: string, data: Partial<CreateJournalEntryRequest>): Promise<JournalEntry> {
        const response = await api.patch(`/journal-entries/${id}`, data);
        return response.data;
    },

    async delete(id: string): Promise<void> {
        await api.delete(`/journal-entries/${id}`);
    },
};

// Meal Logs API
export const mealLogsApi = {
    async create(data: CreateMealLogRequest): Promise<MealLog> {
        const response = await api.post('/meal-logs', data);
        return response.data;
    },

    async getMyMeals(params?: {
        startDate?: string;
        endDate?: string;
        mealType?: string;
        mealSource?: string;
    }): Promise<MealLog[]> {
        const searchParams = new URLSearchParams();
        if (params?.startDate) searchParams.append('startDate', params.startDate);
        if (params?.endDate) searchParams.append('endDate', params.endDate);
        if (params?.mealType) searchParams.append('mealType', params.mealType);
        if (params?.mealSource) searchParams.append('mealSource', params.mealSource);

        const response = await api.get(`/meal-logs/my-meals?${searchParams.toString()}`);
        return response.data;
    },

    async getMyDailyMeals(date: string): Promise<MealLog[]> {
        const response = await api.get(`/meal-logs/my-daily/${date}`);
        return response.data;
    },

    async getMyDailyPointsSummary(date: string): Promise<DailyPointsSummary> {
        const response = await api.get(`/meal-logs/my-daily-summary/${date}`);
        return response.data;
    },

    async getMyMealStats(): Promise<MealStats> {
        const response = await api.get('/meal-logs/my-meal-stats');
        return response.data;
    },

    async getById(id: string): Promise<MealLog> {
        const response = await api.get(`/meal-logs/${id}`);
        return response.data;
    },

    async delete(id: string): Promise<void> {
        await api.delete(`/meal-logs/${id}`);
    },
};

// Error handling utility
export const handleApiError = (error: any): string => {
    if (error.response?.data?.message) {
        return error.response.data.message;
    }
    if (error.message) {
        return error.message;
    }
    return 'An unexpected error occurred';
};
